/**
 * Column keys and DataGrid configuration for nested J-code rows in order-auth tables.
 */
import { DataGridColumn, TagOption } from "@risalabs_frontend_org/oasis-ui-kit";
import { Colors } from "risa-oasis-ui_v2";
import { AuthStatusOptionModel } from "../../data-model/authStatusOptions";
import { AuthOnFile } from "../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../enums/medicalPaOrdersAuthStatus";
import { getAllJCodesFromAuthOnFile } from "../../pages/singleOrderLayout/utils/existingDocsToLs";
import { ExpandedAuthDataGridRow } from "./types";

/** Row-data keys for nested J-code DataGrid columns. */
export enum ExpandedAuthTableRowColumnKeys {
  ORDER_ID = "orderId",
  JCODE = "jCode",
  DESCRIPTION = "description",
  VISITS = "visits",
  REMAINING_VISITS = "remainingVisits",
  COMPLETED_VISITS = "completedVisits",
  /** Auth status id matched against DataGrid tag option values. */
  AUTH_STATUS = "authStatus",
  /** Legacy key; DataGrid rows use {@link ExpandedAuthDataGridRow.id} instead. */
  JCODE_ID = "jCodeId",
}

/** Default tag styling when a J-code auth status id is not in {@link TagOption} list. */
const AUTH_STATUS_FALLBACK_TAG_STYLE: Pick<
  TagOption,
  "backgroundColor" | "textColor" | "showBorder"
> = {
  backgroundColor: Colors.tertiaryBlue12,
  textColor: Colors.tertiaryBlue4,
  showBorder: false,
};

/**
 * Maps configured auth status options to Oasis DataGrid tag options.
 *
 * @param authStatusOptions - Auth status options from Redux
 * @returns Tag options keyed by auth status id
 */
export const mapAuthStatusOptionsToTagOptions = (
  authStatusOptions: AuthStatusOptionModel[],
): TagOption[] => {
  const configuredOptions = authStatusOptions.map(
    (option): TagOption => ({
      label: option.text,
      value: option.id,
      backgroundColor: option.bgColor,
      textColor: option.textColor,
    }),
  );

  const fallbackOptions: TagOption[] = [
    {
      label: "Not Available",
      value: MedicalPaOrdersAuthStatus.NotAvailable,
      backgroundColor: Colors.primaryGray16,
      textColor: Colors.grayscaleBlack1,
    },
    {
      label: "New",
      value: MedicalPaOrdersAuthStatus.New,
      backgroundColor: Colors.tertiaryBlue12,
      textColor: Colors.tertiaryBlue4,
    },
  ];

  const existingValues = new Set(
    configuredOptions.map((option) => option.value),
  );
  const mergedFallbacks = fallbackOptions.filter(
    (option) => !existingValues.has(option.value),
  );

  return [...configuredOptions, ...mergedFallbacks];
};

/**
 * Builds DataGrid column definitions for nested J-code auth rows.
 *
 * @param tagOptions - Auth status tag options for the status column
 * @returns DataGrid columns with widths summing to 100%
 */
export const buildExpandedAuthDataGridColumns = (
  tagOptions: TagOption[],
): DataGridColumn<ExpandedAuthDataGridRow>[] => [
  {
    key: ExpandedAuthTableRowColumnKeys.JCODE,
    header: "J-Code",
    width: "12%",
    truncate: true,
  },
  {
    key: ExpandedAuthTableRowColumnKeys.DESCRIPTION,
    header: "Description",
    width: "40%",
    truncate: true,
  },
  {
    key: ExpandedAuthTableRowColumnKeys.VISITS,
    header: "Visits",
    width: "12%",
  },
  {
    key: ExpandedAuthTableRowColumnKeys.REMAINING_VISITS,
    header: "Remaining",
    width: "12%",
  },
  {
    key: ExpandedAuthTableRowColumnKeys.COMPLETED_VISITS,
    header: "Completed",
    width: "12%",
  },
  {
    key: ExpandedAuthTableRowColumnKeys.AUTH_STATUS,
    header: "Auth Status",
    width: "12%",
    type: "tag",
    tagOptions,
    fallbackTagStyle: AUTH_STATUS_FALLBACK_TAG_STYLE,
  },
];

/**
 * Builds nested DataGrid rows for J-codes on an expanded order-auth row.
 *
 * Each row uses the J-code id as {@link ExpandedAuthDataGridRow.id} and stores
 * the raw auth status id in {@link ExpandedAuthDataGridRow.authStatus} for tag lookup.
 *
 * @param authOnFileData - Auth-on-file payload for the parent order row
 * @returns J-code DataGrid rows, or an empty array when no codes exist
 */
export const generateExpandedAuthDataGridRows = (
  authOnFileData: AuthOnFile,
): ExpandedAuthDataGridRow[] => {
  const allJCodes = getAllJCodesFromAuthOnFile(authOnFileData);
  return (
    allJCodes?.map((jCode): ExpandedAuthDataGridRow => {
      const authStatusId =
        jCode?.auth_status ?? MedicalPaOrdersAuthStatus.NotAvailable;

      return {
        id: jCode?.id ?? "",
        jCode: jCode?.j_code ?? "",
        description: jCode?.medicine_name ?? "",
        visits: jCode?.num_visits ?? "",
        remainingVisits: jCode?.visits_remaining ?? "",
        completedVisits: jCode?.visits_happened ?? "",
        authStatus: authStatusId,
      };
    }) ?? []
  );
};
